import React from "react";
import { connect } from "react-redux";
import { compose } from "redux";
import { ModuleBase } from '@loanbossinc/component-library';
import { withRouter } from "react-router";
import { withLDConsumer } from "launchdarkly-react-client-sdk";
import { propertyGetByDeal, propertyGetDeal, propertyGetDealMetrics } from './property.store';
import { PropertyActiveTable } from "./propertyActiveTable";
import { PropertySoldTable } from "./propertySoldTable";
import styled from "styled-components";
import { StyledModuleFooter } from "component-lib/ModuleFooter";
import {
  SecondaryButton
  , DefaultModuleHeader
  , ButtonSizeOptions
  , DEAL_STATUSES
  , ButtonText
  , MetricSummaryGrid
  , mapMetricFromList
  , DEAL_METRICS
  , ModuleEmptyButton
  , HomeWork
  , ICONS
  , GUEST_PERMISSIONS
} from '@loanbossinc/component-library';
import { Collapse } from "@material-ui/core";
import featureFlags from "../../constants/featureFlags";
import { getDealMetric, getValueMetric } from "utils/metrics";
import { getEnvConfig } from "helpers/environment-config";

const StyledMetricSummaryGrid = styled(MetricSummaryGrid)`
  && {
    position: relative;

    div {
      display: flex;
      flex: 1;
      border-top: none;
    }

    div:first-child {
      border-left: none;
      border-right: none;
    }

    div:last-child {
      border-left: none;
      border-right: none;
    }
  }
`;

const StyledTitleWrapper = styled.div`
  && {
    span.sold-properties {
      font-size: 20px;
      color: #5F6368;
      margin-left: 4px;
    }
  }
`;

const ACTIVE_STATE = 'ACTIVE';
const SOLD_STATE = 'SOLD';
const ACTIVE_AND_SOLD_STATE = 'ACTIVE-SOLD';
class PropertyModule extends React.Component {
  state = {
    activeProperties: []
    , soldProperties: []
    , showSummary: true
    , isExpanded: false
    , currentState: null
  }

  componentDidMount() {
    const { dealId } = this.props.match.params;

    this.props.propertyGetDeal({ dealId });

    this.props.propertyGetByDeal({ dealId });
    this.props.propertyGetDealMetrics({ dealId });
  }

  componentDidUpdate(prevProps) {
    const { properties, deal } = this.props;
    if (
      this.hasDeal()
      && !!properties.length
      && (deal.id !== prevProps.deal.id || properties !== prevProps.properties)
    ) {
      this.updateLists();
    }
  }

  toggleIsExpanded = () => this.setState({ ...this.state, isExpanded: !this.state.isExpanded });
  toggleShowSummary = () => this.setState({ ...this.state, showSummary: !this.state.showSummary });
  hasDeal = () => !!this.props.deal && !!this.props.deal.id;

  getSummaryMetrics = () => {
    const { dealMetrics } = this.props;
    const { activeProperties } = this.state;
    if (!activeProperties || !activeProperties.length) return null;

    const useNewMetrics =
      this.props.flags[featureFlags.GLA453_CLIENTS_WHO_USE_NEW_METRICS]?.includes(this.props.authentication.user.contact.companyId) ||
      this.props.flags[featureFlags.GLA453_USE_NEW_METRICS] ||
      (this.props.flags[featureFlags.GLA453_SYSTEM_ADMINISTRATORS_USE_NEW_METRICS] && this.props.authentication.isSystemAdmin);

    // TODO: need to clean up metric formatting / metric fetch to only return the number and not formatted strings
    const occupancyMetricValue = getDealMetric(this.props, useNewMetrics ? DEAL_METRICS.OCCUPANCY_NEW : DEAL_METRICS.OCCUPANCY),
      valueMetric = getValueMetric(this.props, useNewMetrics),
      valueMetricValue = mapMetricFromList(valueMetric.id, dealMetrics);

    return [
      {
        title: valueMetric.label
        , metricValue: valueMetricValue
        , type: 'CURRENCY'
      }, {
        title: "occupancy"
        , metricValue: occupancyMetricValue && occupancyMetricValue.decimalValue
        , type: 'PERCENT_RING'
      }, {
        title: "noi"
        , metricValue: mapMetricFromList(useNewMetrics ? DEAL_METRICS.NOI_ACTUAL_NEW : DEAL_METRICS.NOI_ACTUAL, dealMetrics)
        , type: 'CURRENCY'
      }
    ];
  };

  updateLists = () => {
    const { properties, deal } = this.props;

    // in the case that the deal is sold, then all properties are considered sold.
    if (deal.dealStatusId === DEAL_STATUSES.SOLD) {
      this.setState({ ...this.state, soldProperties: properties, currentState: SOLD_STATE });
    } else {
      const activeProperties = properties.filter(prop => !prop.actualExitDate);
      const soldProperties = properties.filter(prop => !!prop.actualExitDate);

      let currentState = `${activeProperties.length ? ACTIVE_STATE : ''} ${soldProperties.length ? SOLD_STATE : ''}`;
      currentState = currentState.trim();
      currentState = currentState.replace(' ', '-');

      this.setState({
        ...this.state
        , activeProperties
        , soldProperties
        , currentState
        , showSummary: !!activeProperties && !!activeProperties.length
      });
    }
  }

  getHeaderButton = () => {
    const { showSummary, activeProperties } = this.state;
    const isOneProperty = !!activeProperties && activeProperties.length === 1;
    if (!activeProperties || !activeProperties.length) return null;

    const detailButton = isOneProperty ? 'Show Details' : 'Show List';
    const label = showSummary ? detailButton : 'Show Summary';
    const onClick = isOneProperty ? () => this.goToProperty(activeProperties[0].id) : this.toggleShowSummary;

    return (
      <SecondaryButton
        label={label}
        size={ButtonSizeOptions.smFluid}
        onClick={onClick}
      />
    );
  }

  getTitleInfo = () => {
    const { activeProperties, soldProperties, currentState } = this.state;

    if (!currentState) return { title: null, subTitle: null };

    const list = currentState === ACTIVE_STATE ? activeProperties : soldProperties;

    if (currentState !== ACTIVE_AND_SOLD_STATE) {
      if (list.length === 1) {
        const prop = list[0];

        return {
          title: prop.address
          , subTitle: `${prop.city}, ${prop.stateRef ? prop.stateRef.value : ''} ${prop.zip || ''}`
        }
      }

      return {
        title: `${list.length} ${list.length > 1 ? 'Properties' : 'Property'}`
        , subTitle: null
      }
    }

    // handles ACTIVE_AND_SOLD_STATE
    const title = (
      <StyledTitleWrapper>
        {!!activeProperties.length && <span className="active-properties">{activeProperties.length} {`${activeProperties.length > 1 ? 'Properties' : 'Property'}`}</span>}
        {!!soldProperties.length && <span className="sold-properties">(and {soldProperties.length} sold)</span>}
      </StyledTitleWrapper>
    )

    return { title };
  }

  renderFooterButton = () => {
    const { guestPermission } = this.props;
    const { activeProperties, soldProperties, currentState, isExpanded, showSummary } = this.state;
    if (!currentState) return null;

    let buttonLabel = null;
    const iconName = isExpanded ? ICONS.UNFOLD_MORE : ICONS.UNFOLD_LESS;
    const switchKey = `${currentState}-${+isExpanded}-${+showSummary}`;
    // 0 denotes collapsed state
    // 1 denotes expanded state
    switch (switchKey) {
      case 'ACTIVE-0-0':
      case 'SOLD-0-0':
        const list = currentState === ACTIVE_STATE ? activeProperties : soldProperties;
        const remaining = list.length - 3;
        buttonLabel = remaining <= 0 ? null : `${remaining} more properties`;
        break;

      case 'ACTIVE-SOLD-0-1':
      case 'SOLD-0-1':
        if (soldProperties.length) buttonLabel = `${soldProperties.length} sold properties`;
        break;

      case 'ACTIVE-SOLD-0-0':
        const remainingActive = activeProperties.length - 3;
        buttonLabel = remainingActive <= 0 ? '' : `${remainingActive} more properties`;

        if (remainingActive <= 0) buttonLabel = `${soldProperties.length} sold properties`;
        else buttonLabel = soldProperties <= 0 ? buttonLabel : `${buttonLabel} (and ${soldProperties.length} sold)`;

        buttonLabel = buttonLabel.trim();
        break;

      case 'ACTIVE-1-0':
      case 'ACTIVE-1-1':
      case 'SOLD-1-0':
      case 'SOLD-1-1':
      case 'ACTIVE-SOLD-1-0':
      case 'ACTIVE-SOLD-1-1':
        buttonLabel = 'Show less';
        break;

      default:
        buttonLabel = null;
        break;
    }

    return (
      <StyledModuleFooter>
        <ButtonText label={'Add a property'} onClick={() => this.goToProperty('new')} />
        {buttonLabel && <ButtonText label={buttonLabel} alignRight iconName={iconName} onClick={this.toggleIsExpanded} />}
      </StyledModuleFooter>
    );
  }

  goToProperty = async (id) => {
    const { dealId } = this.props.match.params;

    const shouldRedirectToNewPropertyPage = this.props.flags[featureFlags.BAR2104_REDIRECT_TO_NEW_PROPERTY_VIEW_PAGE];

    if (shouldRedirectToNewPropertyPage) {
      const newFrontendUrl = await getEnvConfig("newFrontendUrl");
      const url = `${newFrontendUrl}/deals/${dealId}/properties/${id}`;

      window.location.assign(url);
    }
    else {
      const url = `/properties/${id}/${dealId}`;

      this.props.history.push(url);
    }
  };

  renderSoldTable = () => {
    const { history } = this.props;
    const { soldProperties, currentState, isExpanded, showSummary } = this.state;

    const isSummaryAndExpanded = showSummary && isExpanded;
    const isSoldState = currentState && currentState === SOLD_STATE;
    const isActiveAndSoldAndExpanded = currentState === ACTIVE_AND_SOLD_STATE && isExpanded;

    const shouldShow = isSummaryAndExpanded || isSoldState || isActiveAndSoldAndExpanded;
    const properties = isExpanded ? soldProperties : soldProperties.slice(0, 3);

    return (
      <Collapse in={shouldShow}>
        <PropertySoldTable history={history} properties={properties} redirect={this.goToProperty} />
      </Collapse>
    );
  }

  renderActiveTable = () => {
    const { history } = this.props;
    const { activeProperties, currentState, isExpanded, showSummary } = this.state;

    const isActiveState = currentState && currentState !== ACTIVE_STATE && !showSummary;
    const IsActiveActiveAndSold = currentState && ACTIVE_AND_SOLD_STATE && !showSummary;
    const shouldShow = isActiveState || IsActiveActiveAndSold;

    const properties = isExpanded ? activeProperties : activeProperties.slice(0, 3);

    return (
      <Collapse in={shouldShow}>
        <PropertyActiveTable history={history} properties={properties} redirect={this.goToProperty} />
      </Collapse>
    );
  }

  renderSummary = () => {
    const { showSummary } = this.state;

    return (
      <Collapse in={showSummary}>
        <StyledMetricSummaryGrid options={this.getSummaryMetrics()} />
      </Collapse>
    );
  }

  render() {
    const { properties, guestPermission, authentication } = this.props;
    if (!properties || !properties.length) return (
      <ModuleEmptyButton
        style={{ marginTop: '2em', width: '648px' }}
        label="Add a property"
        iconName={<HomeWork />}
        onClick={() => this.goToProperty('new')}
      />
    );

    const { title, subTitle } = this.getTitleInfo();

    return (
      <>
        <ModuleBase>
          <DefaultModuleHeader
            headerElement={[<HomeWork />]}
            headerTitle={title}
            subTitle={subTitle}
            button={this.getHeaderButton()}
          />

          {this.renderSummary()}
          {this.renderActiveTable()}
          {this.renderSoldTable()}
          {
            (
              !authentication.isGuest ||
              (authentication.isGuest && guestPermission && guestPermission.portfolioPermissionId === GUEST_PERMISSIONS.EDIT)
            ) && this.renderFooterButton()
          }
        </ModuleBase>
      </>
    );
  }
}

const mapStateToProps = ({ propertyModule, authentication }) => ({
  ...propertyModule
  , authentication
  , ...propertyModule.propertyModuleReducer
});

export default compose(
  withLDConsumer(),
  withRouter
  , connect(mapStateToProps, {
    propertyGetByDeal
    , propertyGetDeal
    , propertyGetDealMetrics
  })
)(PropertyModule);
